import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatNativeDateModule } from '@angular/material/core';

import { SharedModule } from '@shared/shared.module';

import { FORMLY_CONFIG, FormlyModule } from '@ngx-formly/core';
import { FormlyMatDatepickerModule } from '@ngx-formly/material/datepicker';
import { FormlyMatNativeSelectModule } from '@ngx-formly/material/native-select';
import { CreditRoutingModule } from './credit-routing.module';
import { HistoryCreditComponent } from './history-credit/history-credit.component';
import { HistoricComponent } from './history-credit/historic/historic.component';
import { NuevaSolicitudComponent } from './nueva-solicitud/nueva-solicitud.component';
import { SimulatorComponent } from './simulator/simulator.component';
import { DataValidationComponent } from './data-validation/data-validation.component';
import { YourAcceptanceComponent } from './data-validation/your-acceptance.component';
import { ProductValidationsComponent } from './loan/product-validations/product-validations.component';
import { DecisionModelComponent } from './decision-model';
import { GrayZoneComponent, SimulationModalComponent, SignApplicationComponent } from './finalDecision';
import { ModalConditionsAndTermsComponent } from './data-validation/modal-conditions-and-terms.component';
import { ConfirmDateOfIssueComponent } from './finalDecision/confirmDocumentIssue/confirm-date-of-issue.component';
import { SimulatorService } from './simulator/simulator.service';
import { ModalMissingFieldsComponent } from './data-validation/modal-missing-fields/modal-missing-fields.component';
import { RapiCurrencyInputComponent } from '../core/components/rapi-currency-input/rapi-currency-input.component';
import { ValidatorService } from '../core/components/validator.service';
import { MinimalCurrencyValidation } from '../account/services/async-validator.service';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { NgxCurrencyModule } from 'ngx-currency';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { RapicreditSkeletonModule } from '@core/components/rapicredit-skeleton/rapicredit-skeleton.module';
import { ExtendTermComponent } from './extend-term';
import { ValueToPayComponent } from './value-to-pay';
import { RapiflexNowComponent } from './rapiflex-now';
import { ApplicationTermsAndConditionsComponent } from './application-widgets/application-terms-and-conditions/application-terms-and-conditions.component';
import { CardErrorComponent, CardExplainComponent, CardTermsAndConditionsComponent } from './onboarding-card';
import { ApplicationRejectionComponent } from './application-widgets';
import { DispersionMethodComponent } from './dispersion-method';
import { ReferencesComponent } from './references';
import { InsuranceNotificationComponent } from '../insurance-notification';
import { RefinanceLoanComponent } from './refinance-loan';
import { RefinanceRapiflexComponent } from './refinance-rapiflex';

@NgModule({
  declarations: [
    HistoryCreditComponent,
    HistoricComponent,
    NuevaSolicitudComponent,
    SimulatorComponent,
    DataValidationComponent,
    YourAcceptanceComponent,
    GrayZoneComponent,
    ProductValidationsComponent,
    DecisionModelComponent,
    ModalConditionsAndTermsComponent,
    ConfirmDateOfIssueComponent,
    ModalMissingFieldsComponent,
    ExtendTermComponent,
    ValueToPayComponent,
    RapiflexNowComponent,
    ApplicationTermsAndConditionsComponent,
    CardErrorComponent,
    CardExplainComponent,
    CardTermsAndConditionsComponent,
    ApplicationRejectionComponent,
    DispersionMethodComponent,
    ReferencesComponent,
    GrayZoneComponent,
    SimulationModalComponent,
    SignApplicationComponent,
    InsuranceNotificationComponent,
    RefinanceLoanComponent,
    RefinanceRapiflexComponent,
  ],
  exports: [ConfirmDateOfIssueComponent],
  providers: [
    SimulatorService,
    ValidatorService,
    DatePipe,
    CurrencyPipe,
    { provide: FORMLY_CONFIG, multi: true, useFactory: MinimalCurrencyValidation, deps: [ValidatorService] },
    { provide: 'Window', useValue: window },
  ],
  imports: [
    CommonModule,
    SharedModule,
    CreditRoutingModule,
    MatMenuModule,
    MatIconModule,
    MatCardModule,
    MatTableModule,
    MatDatepickerModule,
    MatTooltipModule,
    MatSliderModule,
    FormsModule,
    MatStepperModule,
    MatNativeDateModule,
    MatMomentDateModule,
    FormlyModule.forRoot({
      validationMessages: [
        { name: 'required', message: 'Campo requerido' },
        { name: 'minlength', message: minlengthValidationMessage },
        { name: 'maxlength', message: maxlengthValidationMessage },
        { name: 'min', message: minValidationMessage },
        { name: 'max', message: maxValidationMessage },
        { name: 'pattern', message: patternlengthValidationMessage },
        { name: 'server-error', message: serverError },
      ],
      types: [{ name: 'currency', component: RapiCurrencyInputComponent, wrappers: ['form-field'] }],
    }),
    FormlyMaterialModule,
    FormlyMatDatepickerModule,
    FormlyMatNativeSelectModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    MatDialogModule,
    MatButtonModule,
    MatDatepickerModule,
    MatTooltipModule,
    PdfViewerModule,
    NgxCurrencyModule,
    RapicreditSkeletonModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CreditModule {}

export function minlengthValidationMessage(err, field) {
  return `Should have atleast ${field.templateOptions.minLength} characters`;
}

export function maxlengthValidationMessage(err, field) {
  return `This value should be less than ${field.templateOptions.maxLength} characters`;
}

export function patternlengthValidationMessage(err, field) {
  return `message: ${field.validation.messages.pattern}`;
}

export function minValidationMessage(err, field) {
  return `This value should be more than ${field.templateOptions.min}`;
}

export function maxValidationMessage(err, field) {
  return `This value should be less than ${field.templateOptions.max}`;
}

export function serverError(err, field) {
  return field;
}
